import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PlayCircle, RotateCcw, Clock, MessageSquare, Layers, FileText, Gauge } from 'lucide-react';
import type { DiscoveryMethod } from '@/components/values/DiscoveryMethodSelector';
import type { ValueAssessmentDraft, CardSortDraftData, ReflectionDraftData, QuickAssessmentDraftData, ChatDraftData } from '@/hooks/useValueAssessmentDraft';

interface ResumeDraftPromptProps {
  draft: ValueAssessmentDraft;
  onResume: () => void;
  onStartFresh: () => void;
}

const METHOD_INFO: Record<DiscoveryMethod, { label: string; icon: typeof MessageSquare }> = {
  'chat': { label: 'AI Conversation', icon: MessageSquare },
  'card-sort': { label: 'Values Card Sort', icon: Layers },
  'reflection': { label: 'Reflection Prompts', icon: FileText },
  'assessment': { label: 'Quick Assessment', icon: Gauge },
};

function getProgressSummary(draft: ValueAssessmentDraft): string {
  const data = draft.draft_data;

  switch (draft.method) {
    case 'card-sort': {
      const d = data as CardSortDraftData;
      const sorted = Object.values(d.categories || {}).filter(c => c !== 'unassigned').length;
      const total = Object.keys(d.categories || {}).length || 30;
      if (d.step === 'narrow') {
        return `${d.topFive?.length || 0} of 5 top values selected`;
      }
      return `${sorted} of ${total} values sorted`;
    }
    case 'reflection': {
      const d = data as ReflectionDraftData;
      const answered = (d.answers || []).filter(a => a.trim().length > 0).length;
      return `${answered} of 5 questions answered`;
    }
    case 'assessment': {
      const d = data as QuickAssessmentDraftData;
      const rated = Object.keys(d.ratings || {}).length;
      if (d.showResults) return 'Review your results';
      return `${rated} of 15 statements rated`;
    }
    case 'chat': {
      const d = data as ChatDraftData;
      const count = d.messages?.length || 0;
      return `${count} message${count !== 1 ? 's' : ''} in conversation`;
    }
    default:
      return 'In progress';
  }
}

function getTimeSince(dateString: string): string {
  const diff = Date.now() - new Date(dateString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export const ResumeDraftPrompt = ({ draft, onResume, onStartFresh }: ResumeDraftPromptProps) => {
  const [confirmFresh, setConfirmFresh] = useState(false);
  const methodInfo = METHOD_INFO[draft.method];
  const Icon = methodInfo.icon;

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">{methodInfo.label}</span>
              <Badge variant="secondary" className="text-xs gap-1">
                <Clock className="w-3 h-3" />
                {getTimeSince(draft.updated_at)}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {getProgressSummary(draft)}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={onResume} className="flex-1 gap-2">
            <PlayCircle className="w-4 h-4" />
            Resume Where I Left Off
          </Button>
          {confirmFresh ? (
            <Button
              variant="destructive"
              size="sm"
              onClick={onStartFresh}
              className="gap-1"
            >
              Confirm
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmFresh(true)}
              className="gap-1 text-muted-foreground"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Start Fresh
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
